import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

// Types
type ClaimSummary = {
  id: number;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
  user: { email: string; firstName: string | null; lastName: string | null } | null;
};

type ClaimMessage = {
  id: number;
  sender: string;
  message: string;
  createdAt: string;
  user: { firstName: string | null; lastName: string | null; email: string } | null;
};

type ClaimAttachment = { id: number; fileName: string; fileKey: string };

type ClaimDetail = {
  id: number;
  subject: string;
  description: string;
  status: string;
  priority: string;
  createdAt: string;
  adminNotes: string | null;
  registration: { serialNumber: string; product: { name: string } | null } | null;
  user: { email: string; firstName: string | null; lastName: string | null } | null;
  claimMessages: ClaimMessage[];
  claimAttachments: ClaimAttachment[];
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  open:      { bg: '#dbeafe', color: '#1d4ed8' },
  in_review: { bg: '#fef9c3', color: '#a16207' },
  approved:  { bg: '#dcfce7', color: '#15803d' },
  rejected:  { bg: '#fee2e2', color: '#b91c1c' },
  resolved:  { bg: '#f3f4f6', color: '#6b7280' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string }> = {
  low:    { bg: '#f3f4f6', color: '#6b7280' },
  medium: { bg: '#fef9c3', color: '#a16207' },
  high:   { bg: '#fee2e2', color: '#b91c1c' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_COLORS[status] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {status.replace('_', ' ')}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const p = PRIORITY_COLORS[priority] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ background: p.bg, color: p.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {priority}
    </span>
  );
}

function ClaimDetailPanel({ claimId, onClose, onUpdated }: { claimId: number; onClose: () => void; onUpdated: (c: ClaimSummary) => void }) {
  const [claim, setClaim] = useState<ClaimDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = () => {
    setLoading(true);
    api.get<ClaimDetail>(`/admin/claims/${claimId}`)
      .then(c => { setClaim(c); setAdminNotes(c.adminNotes ?? ''); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [claimId]);

  async function updateStatus(status: string) {
    if (!claim) return;
    setUpdatingStatus(true);
    await api.patch(`/admin/claims/${claim.id}/status`, { status });
    setClaim(c => c ? { ...c, status } : c);
    onUpdated({ id: claim.id, subject: claim.subject, status, priority: claim.priority, createdAt: claim.createdAt, user: claim.user });
    setUpdatingStatus(false);
  }

  async function saveNotes() {
    if (!claim) return;
    setSavingNotes(true);
    await api.patch(`/admin/claims/${claim.id}/notes`, { adminNotes });
    setClaim(c => c ? { ...c, adminNotes } : c);
    setSavingNotes(false);
  }

  async function sendReply() {
    if (!claim || !reply.trim()) return;
    setSending(true);
    await api.post(`/admin/claims/${claim.id}/reply`, { message: reply });
    setReply('');
    await load();
    setSending(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end',
      background: 'rgba(0,0,0,0.25)',
    }} onClick={onClose}>
      <div
        style={{ width: '100%', maxWidth: 520, background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontWeight: 700, fontSize: 15, color: '#0b0d0e' }}>Claim #{claimId}</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8f9b9b', lineHeight: 1 }}>×</button>
        </div>

        {loading || !claim ? (
          <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p>
        ) : (
          <div style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Subject & badges */}
            <div>
              <p style={{ fontWeight: 600, fontSize: 15, color: '#0b0d0e', marginBottom: 6 }}>{claim.subject}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusBadge status={claim.status} />
                <PriorityBadge priority={claim.priority} />
              </div>
            </div>

            {/* Meta */}
            <div style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
              {claim.user && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#8f9b9b' }}>Submitted by</span>
                  <span style={{ color: '#374151' }}>{[claim.user.firstName, claim.user.lastName].filter(Boolean).join(' ') || claim.user.email}</span>
                </div>
              )}
              {claim.registration && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#8f9b9b' }}>Product</span>
                  <span style={{ color: '#374151' }}>{claim.registration.product?.name} · <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{claim.registration.serialNumber}</span></span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#8f9b9b' }}>Filed</span>
                <span style={{ color: '#374151' }}>{claim.createdAt.slice(0, 10)}</span>
              </div>
            </div>

            {/* Description */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Description</p>
              <p style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', background: '#fafafa', borderRadius: 8, padding: '10px 14px' }}>{claim.description}</p>
            </div>

            {/* Status update */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Update status</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['open','in_review','approved','rejected','resolved'].map(s => {
                  const c = STATUS_COLORS[s] ?? { bg: '#f3f4f6', color: '#374151' };
                  const isActive = claim.status === s;
                  return (
                    <button key={s} disabled={updatingStatus || isActive}
                      onClick={() => updateStatus(s)}
                      style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, cursor: isActive ? 'default' : 'pointer',
                        background: isActive ? c.bg : '#f3f4f6', color: isActive ? c.color : '#8f9b9b',
                        border: isActive ? `2px solid ${c.color}` : '2px solid transparent', transition: 'all 0.1s' }}>
                      {s.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Messages thread */}
            {claim.claimMessages.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Thread</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {claim.claimMessages.map(m => (
                    <div key={m.id} style={{ background: m.sender === 'admin' ? '#fffbf5' : '#fafafa', borderRadius: 8, padding: '10px 14px', border: m.sender === 'admin' ? '1px solid #fde68a' : '1px solid #f0f0f0' }}>
                      <p style={{ fontSize: 11, color: '#8f9b9b', marginBottom: 4 }}>
                        {m.sender === 'admin' ? 'Admin' : (m.user?.firstName ?? m.user?.email ?? 'User')} · {m.createdAt.slice(0, 10)}
                      </p>
                      <p style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{m.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Reply as admin</p>
              <textarea className="input" rows={3} value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a reply…" style={{ width: '100%', resize: 'vertical' }} />
              <button className="btn-primary text-sm" style={{ marginTop: 8 }} onClick={sendReply} disabled={sending || !reply.trim()}>
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>

            {/* Admin notes */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Internal notes</p>
              <textarea className="input" rows={3} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Internal notes (not visible to user)…" style={{ width: '100%', resize: 'vertical' }} />
              <button className="btn-primary text-sm" style={{ marginTop: 8 }} onClick={saveNotes} disabled={savingNotes}>
                {savingNotes ? 'Saving…' : 'Save notes'}
              </button>
            </div>

            {/* Attachments */}
            {claim.claimAttachments.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#8f9b9b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Attachments</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {claim.claimAttachments.map(a => {
                    const isImage = /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(a.fileName);
                    const url = `/api/files/${encodeURIComponent(a.fileKey)}`;
                    return isImage ? (
                      <a key={a.id} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={a.fileName} style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, border: '1px solid #e5e5e5', objectFit: 'contain', background: '#fafafa' }} />
                        <p style={{ fontSize: 11, color: '#8f9b9b', marginTop: 2 }}>{a.fileName}</p>
                      </a>
                    ) : (
                      <a key={a.id} href={url} target="_blank" rel="noopener noreferrer" download={a.fileName}
                        style={{ color: '#e00000', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        📎 {a.fileName}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminClaims() {
  const [claims, setClaims] = useState<ClaimSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get<ClaimSummary[]>('/admin/claims?limit=200')
      .then(setClaims)
      .finally(() => setLoading(false));
  }, []);

  const filtered = claims.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.subject.toLowerCase().includes(q) || (c.user?.email ?? '').toLowerCase().includes(q);
    const matchStatus = !statusFilter || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  function exportCSV() {
    const token = localStorage.getItem('mw_token');
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? '';
    fetch(`${apiBase}/admin/claims/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'claims.csv'; a.click();
        URL.revokeObjectURL(url);
      });
  }

  function handleUpdated(updated: ClaimSummary) {
    setClaims(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Claims</h1>
        <button onClick={exportCSV} style={{ fontSize: 12, padding: '6px 14px', border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#374151' }}>
          Export CSV
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search subject or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {['open','in_review','approved','rejected','resolved'].map(s => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#8f9b9b', alignSelf: 'center' }}>{filtered.length} results</span>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
        {loading ? <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 540 }}>
              <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr style={{ textAlign: 'left' }}>
                  {['Subject', 'User', 'Priority', 'Status', 'Filed'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}
                    onClick={() => setSelectedClaim(c.id)}
                    style={{ borderBottom: '1px solid #fafafa', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#fafafa'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ fontWeight: 500, color: '#0b0d0e' }}>{c.subject}</p>
                      <p style={{ fontSize: 11, color: '#8f9b9b' }}>#{c.id}</p>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ color: '#374151' }}>{[c.user?.firstName, c.user?.lastName].filter(Boolean).join(' ') || '—'}</p>
                      <p style={{ fontSize: 11, color: '#8f9b9b' }}>{c.user?.email}</p>
                    </td>
                    <td style={{ padding: '12px 16px' }}><PriorityBadge priority={c.priority} /></td>
                    <td style={{ padding: '12px 16px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '12px 16px', color: '#8f9b9b', fontSize: 12 }}>{c.createdAt.slice(0, 10)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#8f9b9b' }}>No claims found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedClaim !== null && (
        <ClaimDetailPanel
          claimId={selectedClaim}
          onClose={() => setSelectedClaim(null)}
          onUpdated={handleUpdated}
        />
      )}
    </Layout>
  );
}
